import React, { useState } from "react";
import { useOutletContext } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import {
  FaShieldAlt, FaEye, FaSearch, FaInbox,
  FaCalendarAlt, FaFileContract, FaInfoCircle, 
  FaCheckCircle, FaUpload, FaCloudUploadAlt, FaTimes
} from "react-icons/fa";
import axios from "axios";
import { API_BASE_URL } from "../utils/config";
import { toast } from "react-toastify";

const Agreements = () => {
  const { appliedJobs, searchQuery: globalSearchQuery, setIsModalOpen, setSelectedOffer, setActiveDocTab, fetchDashboardData } = useOutletContext();
  const [localSearchQuery, setLocalSearchQuery] = useState("");
  const [isUploadModalOpen, setIsUploadModalOpen] = useState(false);
  const [selectedApp, setSelectedApp] = useState(null);
  const [isUploading, setIsUploading] = useState(false);
  const [selectedFile, setSelectedFile] = useState(null);

  // Filter logic: Only show apps where the admin has sent an agreement
  const agreementApps = appliedJobs.filter(app => {
    const hasAgreementText = app.adminAgreements && String(app.adminAgreements).trim().length > 0;
    const hasAttachment = app.adminAttachment && String(app.adminAttachment).trim().length > 0;
    const hasDocHistory = app.documentHistory && app.documentHistory.some(d =>
      d.documentType === "Agreement" || d.documentType === "agreement"
    );
    return hasAgreementText || hasAttachment || hasDocHistory;
  }).filter(app => {
    const combinedQuery = (globalSearchQuery || localSearchQuery).toLowerCase();
    return (app.jobId?.role || "").toLowerCase().includes(combinedQuery);
  });

  const handleOpenUpload = (app) => {
    setSelectedApp(app);
    setIsUploadModalOpen(true);
  };

  const handleFileUpload = async () => {
    if (!selectedFile || !selectedApp) return;

    setIsUploading(true);
    const formData = new FormData();
    formData.append("signedDoc", selectedFile);
    formData.append("applicationId", selectedApp._id);

    try {
      const token = localStorage.getItem("candidateToken");
      const res = await axios.post(`${API_BASE_URL}/applications/upload-signed-docs`, formData, {
        headers: {
          "Content-Type": "multipart/form-data",
          Authorization: `Bearer ${token}`
        }
      });

      if (res.data.success) {
        toast.success("Agreement uploaded successfully!");
        setIsUploadModalOpen(false);
        setSelectedFile(null);
        fetchDashboardData(); // Refresh data
      }
    } catch (err) {
      console.error("Upload error:", err);
      toast.error(err.response?.data?.message || "Failed to upload document.");
    } finally {
      setIsUploading(false);
    }
  };

  return (
    <div className="w-full h-full space-y-8">
      
      {/* ── Header & Filters ── */}
      <div className="bg-white rounded-2xl shadow-sm border border-gray-100 p-8 mb-4">
        <div className="flex flex-wrap items-center gap-4">
          
          <div className="flex items-center gap-4 mr-auto">
             <div className="p-3 bg-gradient-to-br from-indigo-50 to-purple-50 rounded-xl text-indigo-600 shadow-sm border border-indigo-100/50">
                <div className="w-10 h-10 rounded-full bg-gradient-to-r from-indigo-500 to-purple-600 flex items-center justify-center text-white shadow-md">
                   <FaFileContract size={20} />
                </div>
             </div>
             <div>
                <h1 className="text-2xl font-bold bg-clip-text text-transparent bg-gradient-to-r from-green-500 to-blue-600 tracking-tight">SIGN AGREEMENTS</h1>
                <div className="text-[10px] font-bold text-gray-400 uppercase tracking-widest mt-0.5">Execution & Signing Vault</div>
             </div>
          </div>

          <div className="relative flex-1 min-w-[200px] max-w-sm">
            <FaSearch className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400 text-xs" />
            <input
              type="text"
              placeholder="Search agreements..."
              value={localSearchQuery}
              onChange={(e) => setLocalSearchQuery(e.target.value)}
              className="w-full pl-9 pr-4 py-2 text-xs font-bold border border-gray-200 rounded-xl focus:ring-2 focus:ring-blue-500 outline-none uppercase tracking-widest transition-all"
            />
          </div>

          {localSearchQuery && (
            <button
              onClick={() => setLocalSearchQuery("")}
              className="h-8 px-4 text-[10px] font-bold text-gray-600 bg-gray-50 border border-gray-200 rounded-lg hover:bg-gray-100 transition uppercase tracking-widest"
            >
              Clear
            </button>
          )}
        </div>
      </div>

      {/* ── Table Area ── */}
      <div className="p-0 bg-white border border-gray-100 shadow-lg rounded-2xl overflow-hidden">
        {agreementApps.length > 0 ? (
          <div className="overflow-x-auto">
            <table className="min-w-full">
              <thead className="text-[11px] text-white bg-gradient-to-r from-green-500 to-blue-600 uppercase tracking-widest font-bold">
                <tr>
                  <th className="py-4 px-6 text-center font-bold">Agreement Context</th>
                  <th className="py-4 px-6 text-center font-bold">Status Profile</th>
                  <th className="py-4 px-6 text-center font-bold">Temporal Sync</th>
                  <th className="py-4 px-6 text-center font-bold">Protocol Commands</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-50">
                {agreementApps.map((app) => {
                  const isSigned = !!app.candidateAgreementsUpload;
                  const isAccepted = app.docReviewStatus === "Accepted";
                  const isRejected = app.docReviewStatus === "Rejected";

                  return (
                    <tr key={app._id} className="hover:bg-gray-50/50 transition-colors">
                      <td className="px-6 py-4 text-center">
                        <div className="text-xs font-bold text-gray-800 tracking-tight">{app.jobId?.role || "Position Detail"}</div>
                        <div className="text-[9px] font-bold text-gray-400 uppercase tracking-widest mt-1.5 flex items-center justify-center gap-1.5">
                          <FaShieldAlt className="text-indigo-400 text-[9px]" /> LEGAL BINDING CONTRACT
                        </div>
                      </td>
                      <td className="px-6 py-4 text-center">
                        {!isSigned ? (
                           <span className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full bg-orange-50 text-orange-600 text-[10px] font-bold uppercase tracking-widest border border-orange-100 shadow-sm">
                              <div className="w-1.5 h-1.5 rounded-full bg-orange-600 animate-pulse" />
                              Action Required
                           </span>
                        ) : isAccepted ? (
                           <span className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full bg-emerald-50 text-emerald-600 text-[10px] font-bold uppercase tracking-widest border border-emerald-100 shadow-sm">
                              <FaCheckCircle className="text-[12px]" /> Accepted
                           </span>
                        ) : isRejected ? (
                          <span className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full bg-rose-50 text-rose-600 text-[10px] font-bold uppercase tracking-widest border border-rose-100 shadow-sm">
                             <FaTimes className="text-[12px]" /> Rejected
                          </span>
                        ) : (
                           <span className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full bg-blue-50 text-blue-600 text-[10px] font-bold uppercase tracking-widest border border-blue-100 shadow-sm">
                             Under Review
                           </span>
                        )}
                      </td>
                      <td className="px-6 py-4 text-center">
                        <div className="flex flex-col items-center">
                          <span className="inline-flex items-center gap-1.5 text-[10px] font-bold text-gray-600 uppercase tracking-widest">
                            <FaCalendarAlt className="text-indigo-300 text-[10px]" />
                            {new Date(app.updatedAt).toLocaleDateString("en-IN", { day: 'numeric', month: 'short', year: 'numeric' })}
                          </span>
                          <span className="text-[8px] font-bold text-gray-400 uppercase tracking-widest mt-0.5">Last Modification</span>
                        </div>
                      </td>
                      <td className="px-6 py-4 text-center">
                        <div className="flex items-center justify-center gap-4">
                          <button
                            onClick={() => { setSelectedOffer(app); setActiveDocTab("agreement"); setIsModalOpen(true); }}
                            className="w-10 h-10 flex items-center justify-center rounded-xl bg-indigo-50 text-indigo-700 hover:bg-indigo-600 hover:text-white border border-indigo-100 transition-all transform active:scale-90 shadow-sm"
                            title="Interactive View"
                          >
                            <FaEye size={16} />
                          </button>
                          
                          {(!isAccepted) && (
                            <button
                              onClick={() => handleOpenUpload(app)}
                              className="flex items-center gap-2 px-4 py-2.5 bg-gray-900 text-white rounded-xl text-[10px] font-bold uppercase tracking-widest transition-all hover:bg-black active:scale-95 shadow-lg border border-black"
                            >
                              <FaUpload size={13} className="text-gray-400" /> {isSigned ? "Resubmit" : "Upload Signed"}
                            </button>
                          )}
                        </div>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        ) : (
          <div className="p-16 text-center">
            <div className="w-16 h-16 bg-indigo-50 rounded-2xl flex items-center justify-center mx-auto mb-4 text-indigo-200 border border-indigo-100/50 shadow-sm">
              <FaFileContract size={30} />
            </div>
            <h3 className="text-base font-bold text-gray-700 mb-1">No Agreements Available</h3>
            <p className="text-[11px] text-gray-400 font-medium tracking-widest uppercase max-w-xs mx-auto leading-relaxed">Official agreements will appear here once the recruitment team has prepared them for your signature.</p>
          </div>
        )}
      </div>

      <div className="flex items-center justify-center gap-2 text-[9px] font-bold text-gray-400 uppercase tracking-widest pt-2 pb-6">
        <FaInfoCircle className="text-blue-400" />
        Timely Health Secure Signing Portal • {new Date().getFullYear()}
      </div>

      {/* ── Upload Modal ── */}
      <AnimatePresence>
        {isUploadModalOpen && (
          <div className="fixed inset-0 z-[200] flex items-center justify-center p-4 bg-slate-900/60 backdrop-blur-md">
            <motion.div 
              initial={{ opacity: 0, scale: 0.95 }}
              animate={{ opacity: 1, scale: 1 }}
              exit={{ opacity: 0, scale: 0.95 }}
              className="bg-white max-w-md w-full rounded-[2.5rem] shadow-2xl p-8"
            >
              <div className="flex justify-between items-center mb-6">
                <div>
                  <h3 className="text-sm font-bold text-gray-900 uppercase tracking-widest">Sign & Upload</h3>
                  <p className="text-[9px] font-bold text-indigo-500 uppercase tracking-widest mt-1">Official Agreement Protocol</p>
                </div>
                <button onClick={() => setIsUploadModalOpen(false)} className="w-10 h-10 rounded-xl bg-gray-50 flex items-center justify-center text-gray-400 hover:text-gray-600 transition-colors">
                  <FaTimes />
                </button>
              </div>

              <p className="text-[10px] font-bold text-gray-500 uppercase tracking-widest leading-relaxed mb-6">
                Please ensure the document is clear, signed, and in PDF or Word format. This will be reviewed by the HR team for final verification.
              </p>

              <div 
                className={`relative border-2 border-dashed rounded-2xl p-10 flex flex-col items-center justify-center transition-all ${
                  selectedFile ? 'border-indigo-400 bg-indigo-50/30' : 'border-gray-100 hover:border-indigo-300 hover:bg-gray-50'
                }`}
              >
                <input 
                  type="file" 
                  onChange={(e) => setSelectedFile(e.target.files[0])}
                  className="absolute inset-0 opacity-0 cursor-pointer"
                  accept=".pdf,.doc,.docx"
                />
                <FaCloudUploadAlt className={`text-4xl mb-3 ${selectedFile ? 'text-indigo-500' : 'text-gray-300'}`} />
                <p className="text-xs font-bold text-gray-800">
                  {selectedFile ? selectedFile.name : "Choose File or Drag & Drop"}
                </p>
                <p className="text-[9px] font-bold text-gray-400 uppercase mt-1">PDF, DOC up to 5MB</p>
              </div>

              <div className="mt-8 flex gap-3">
                <button 
                  onClick={() => { setIsUploadModalOpen(false); setSelectedFile(null); }}
                  className="flex-1 py-4 text-[10px] font-bold text-gray-400 uppercase tracking-widest rounded-2xl hover:bg-gray-50 transition-colors"
                >
                  Cancel
                </button>
                <button 
                  onClick={handleFileUpload}
                  disabled={!selectedFile || isUploading}
                  className="flex-1 py-4 bg-gradient-to-r from-green-500 to-blue-600 text-white rounded-2xl text-[10px] font-bold uppercase tracking-[0.2em] shadow-lg shadow-blue-100 hover:scale-[1.02] transition-all active:scale-95 disabled:opacity-50"
                >
                  {isUploading ? "Transmitting..." : "Execute Upload"}
                </button>
              </div>
            </motion.div>
          </div>
        )}
      </AnimatePresence>
    </div>
  );
};

export default Agreements;